import {
  DEFAULT_DATA_POINT_RADIUS,
  DEFAULT_FILL_OPACITY,
} from '../../../../constants.js';
import type { PointShapeType } from '../../../shape/type.js';
import {
  type DotCanvasInstance,
  FnCanvasAccessor,
} from '../../types/canvas-shape.js';
import { ARC_BATCH_SIZE } from '../batched-path-context.js';
import { getCanvasColor } from '../canvas-color.js';
import { drawSymbol } from './symbol-canvas.js';
import { POINT_OVERLAY_PADDING } from '../../../data-point/constants.js';

type FnDotCanvasStyle<Datum> = (
  ctx: CanvasRenderingContext2D,
  dotCanvas: DotCanvasInstance<Datum>,
  x: FnCanvasAccessor<Datum>,
  y: FnCanvasAccessor<Datum>,
) => void;

/**
 * Builds style FN Dot based on parameters.
 *
 * @param radius - scatter plot radius
 * @param enableDotOverflow - enable dots indicators to be drawn overflowing the plot area
 * @param shape - The shape to draw. One of 'circle', 'square', 'triangle', or 'diamond'.
 * @returns - fn print Line canvas
 */
export function printDotBuilder<Datum>(
  radius: number = DEFAULT_DATA_POINT_RADIUS,
  enableDotOverflow: boolean,
  shape?: PointShapeType,
): FnDotCanvasStyle<Datum> {
  return (ctx, dotCanvas, x, y) => {
    const {
      color,
      data,
      opacity = DEFAULT_FILL_OPACITY,
      defined = () => true,
    } = dotCanvas;

    const dotColor = getCanvasColor(color(data[0]));
    ctx.save();
    if (enableDotOverflow) {
      ctx.translate(POINT_OVERLAY_PADDING / 2, POINT_OVERLAY_PADDING / 2);
      ctx.save(); // save before doing clipping

      ctx.beginPath();
      ctx.rect(0, 0, dotCanvas.canvasWidth, dotCanvas.canvasHeight);
      ctx.clip();
      ctx.restore();
    }

    ctx.globalAlpha = opacity;
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = dotColor;
    ctx.filter = 'none';

    ctx.beginPath();
    const dataLength = data.length;
    const scatterRadius = radius;
    let arcCount = 0;

    for (let i = 0; i < dataLength; i++) {
      if (
        !defined(data[i]) ||
        y(data[i], i) > dotCanvas.canvasHeight ||
        y(data[i], i) < 0 ||
        x(data[i], i) > dotCanvas.canvasWidth ||
        x(data[i], i) < 0
      ) {
        continue;
      }
      const cx = x(data[i], i);
      const cy = y(data[i], i);

      ctx.moveTo(cx, cy);
      drawSymbol(ctx, cx, cy, scatterRadius, shape);

      arcCount++;
      if (arcCount >= ARC_BATCH_SIZE) {
        ctx.fill();
        ctx.beginPath();
        arcCount = 0;
      }
    }

    ctx.fill();

    ctx.restore();
  };
}
